import Image from 'next/legacy/image';
import { WidenUtil } from '@hub/libs/widen-utils';
import { WidenImageConfig } from '@hub/models/widen-image-config';
import type { widenImageLoaderProps } from '@hub/libs/widen-image-loader';
import { widenImageLoader } from '@hub/libs/widen-image-loader';
import type { WidenCropKeepArea } from '@hub/libs/constants';
import { APP_NAME, WIDEN_DEFAULT_IMAGE_NAME, WIDEN_DEFAULT_IMAGE_TYPE, WIDEN_QUERY_PROPS } from '@hub/libs/constants';

export interface WidenImageProps {
  alt?: string;
  animate?: boolean;
  color?: string;
  crop?: boolean;
  filename?: string;
  height?: number; // width and height are used as attributes for the wrapper
  isPreviewImage?: boolean;
  isVideo?: boolean;
  keep?: WidenCropKeepArea;
  layout?: 'fixed' | 'intrinsic' | 'responsive' | 'fill';
  objectFit?: 'contain' | 'fill' | 'cover' | 'scale-down' | 'none';
  placeholder?: string;
  quality?: number;
  shortCode?: string;
  title?: string;
  width: number; // width and height are used as attributes for the wrapper
  priority?: boolean; // See https://nextjs.org/docs/api-reference/next/image#priority
  mixBlendMode?: boolean; // CSS mix-blend-mode property
}

export type WidenImagePropsKey = keyof WidenImageProps;

// Parameters that are only used in this wrapper component or used to build the Widen URL
const wrapperProps: Array<WidenImagePropsKey> = ['shortCode', 'filename', 'isPreviewImage', 'isVideo'];

export const WidenImage = (props: WidenImageProps) => {
  const {
    shortCode,
    width,
    height,
    alt,
    filename,
    isPreviewImage,
    isVideo,
    placeholder,
    title,
    layout,
    priority = false,
    color,
    animate,
    crop,
    keep,
    mixBlendMode,
  } = props;

  // config for base url only requires shortCode and filename
  // videos have to be parsed as images
  const config = new WidenImageConfig(
    shortCode ?? '',
    isVideo
      ? WidenUtil.replaceFileExtension(filename || WIDEN_DEFAULT_IMAGE_NAME, WIDEN_DEFAULT_IMAGE_TYPE)
      : filename || ''
  );

  const imageSrc = WidenUtil.generateWidenImageUrl(config, placeholder);

  // remove properties that are only used in this wrapper and pass remainder to next/image component
  const filteredProps = Object.fromEntries(
    Object.entries(props).filter(
      entry =>
        !(entry[0] === 'widenId') &&
        !WIDEN_QUERY_PROPS.includes(entry[0] as WidenImagePropsKey) &&
        !wrapperProps.includes(entry[0] as WidenImagePropsKey)
    )
  );

  return (
    <>
      {isVideo && isPreviewImage && (
        <img
          src={`/_${APP_NAME}/icons/play-circle.svg`}
          alt="play icon"
          className="absolute z-20 h-24 w-24 left-1/2 top-1/2 transform -translate-y-1/2 -translate-x-1/2"
          data-testid="play-button"
        />
      )}
      <Image
        loader={() =>
          widenImageLoader({
            src: imageSrc,
            width,
            height,
            color,
            animate,
            crop,
            keep,
          } as widenImageLoaderProps)
        }
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        title={title}
        layout={layout}
        priority={priority}
        unoptimized
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...filteredProps}
        className={mixBlendMode ? 'mix-blend-multiply' : ''}
      />
    </>
  );
};

WidenImage.defaultProps = {
  objectFit: 'contain', // objectFit: contain to make sure aspect ratios are kept unless specified otherwise
  layout: 'fixed',
};
